import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Truck, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";
import { getDriverToken, setDriverToken } from "@/lib/driverAuth";

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg).filter(Boolean).join(" ") : "Couldn't sign in. Check your signal and try again.");

export default function DriverLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (getDriverToken()) return <Navigate to="/driver" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/driver-auth/login`, { username: username.trim(), password });
      setDriverToken(data.token);
      navigate("/driver", { replace: true });
    } catch (err) {
      setError(errText(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="driver-login-page">
      <div className="flex-1 flex items-center justify-center p-5">
        <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#002FA7] grid place-items-center text-white"><Truck className="w-6 h-6" strokeWidth={2.4} /></div>
            <div>
              <div className="font-bold text-xl leading-tight" style={{ fontFamily: "Outfit" }}>Fleet Sheet</div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Driver sign in</div>
            </div>
          </div>
          <div>
            <Label htmlFor="drv-username">Username</Label>
            <Input id="drv-username" value={username} onChange={(e) => setUsername(e.target.value)} autoCapitalize="none" autoCorrect="off" autoComplete="username" spellCheck={false}
              className="mt-1.5 h-12 text-base" placeholder="From your dispatcher" data-testid="driver-login-username" />
          </div>
          <div>
            <Label htmlFor="drv-password">Password</Label>
            <div className="mt-1.5 relative">
              <Input id="drv-password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                className="h-12 text-base pr-12" data-testid="driver-login-password" />
              <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-1 top-1 h-10 w-10 grid place-items-center text-slate-500" aria-label={show ? "Hide password" : "Show password"} data-testid="driver-login-toggle-password">
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {error && <p className="text-sm text-rose-600" data-testid="driver-login-error">{error}</p>}
          <Button type="submit" disabled={busy || !username.trim() || !password} className="w-full h-12 text-base bg-[#002FA7] hover:bg-[#001A75] rounded-xl" data-testid="driver-login-submit">
            {busy && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}Sign in
          </Button>
          <p className="text-xs text-center text-slate-500">Forgot your password? Ask your dispatcher to reset it.</p>
        </form>
      </div>
      <div className="pb-6 text-center text-sm text-slate-500">
        Dispatcher or office? <Link to="/sign-in" className="text-[#002FA7] font-medium" data-testid="driver-login-admin-link">Sign in here</Link>
      </div>
    </div>
  );
}
